import React from "react";
import { FormSection, InputField } from "../../../common/FormComponents";
import {
  Button,
  IconButton,
  Card,
  CardContent,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

type TicketType = {
  id: string;
  name: string;
  price: string;
  quantity: string;
  description: string;
};

type Tickets = {
  remainingTickets: string;
  ticketTypes: TicketType[];
};

type TicketsSectionProps = {
  tickets: Tickets;
  onChange: ({
    field,
    value,
  }: {
    field: keyof Omit<Tickets, "ticketTypes">;
    value: string;
  }) => void;
  onAddTicketType: () => void;
  onUpdateTicketType: ({
    id,
    field,
    value,
  }: {
    id: string;
    field: keyof TicketType;
    value: string;
  }) => void;
  onRemoveTicketType: ({ id }: { id: string }) => void;
  errors?: Record<string, string>;
};

const TicketsSection = ({
  tickets,
  onChange,
  onAddTicketType,
  onUpdateTicketType,
  onRemoveTicketType,
  errors = {},
}: TicketsSectionProps) => {
  const handleChange =
    (field: keyof Omit<Tickets, "ticketTypes">) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ field, value: e.target.value });
    };

  const handleTicketTypeChange =
    (id: string, field: keyof TicketType) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onUpdateTicketType({ id, field, value: e.target.value });
    };

  return (
    <FormSection
      title="Tickets"
      tooltipText="Set up ticket types, prices, and availability for your event"
    >
      <div className="mb-6">
        <InputField
          id="remaining-tickets"
          name="remainingTickets"
          label="Remaining Tickets"
          value={tickets.remainingTickets}
          onChange={handleChange("remainingTickets")}
          error={!!errors.remainingTickets}
          helperText={errors.remainingTickets || ""}
          tooltipText="The total number of tickets available for this event"
        />
      </div>

      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <Typography variant="h6" component="h3">
            Ticket Types
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={onAddTicketType}
            size="small"
            sx={{
              borderColor: "#E1A731",
              color: "#E1A731",
              "&:hover": {
                borderColor: "#c78f1e",
                backgroundColor: "rgba(225, 167, 49, 0.04)",
              },
            }}
          >
            Add Ticket Type
          </Button>
        </div>

        {tickets.ticketTypes.length === 0 ? (
          <Typography
            variant="body2"
            color="textSecondary"
            className="my-4 text-center"
          >
            No ticket types added yet. Click the button above to add a ticket
            type.
          </Typography>
        ) : (
          <div className="space-y-4">
            {tickets.ticketTypes.map((ticketType) => (
              <Card
                key={ticketType.id}
                variant="outlined"
                className="border border-gray-200"
              >
                <CardContent>
                  <div className="flex justify-between items-start mb-2">
                    <Typography
                      variant="subtitle1"
                      component="h4"
                      className="font-semibold"
                    >
                      {ticketType.name || "New Ticket Type"}
                    </Typography>
                    <IconButton
                      aria-label="delete"
                      size="small"
                      onClick={() => onRemoveTicketType({ id: ticketType.id })}
                      sx={{ color: "#ef4444" }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputField
                      id={`ticket-name-${ticketType.id}`}
                      name="name"
                      label="Ticket Name"
                      value={ticketType.name}
                      onChange={handleTicketTypeChange(ticketType.id, "name")}
                      error={!!errors[`ticketName-${ticketType.id}`]}
                      helperText={errors[`ticketName-${ticketType.id}`] || ""}
                      required
                      tooltipText="A name for this ticket type (e.g., 'General Admission', 'VIP', etc.)"
                    />

                    <InputField
                      id={`ticket-price-${ticketType.id}`}
                      name="price"
                      label="Price ($)"
                      value={ticketType.price}
                      onChange={handleTicketTypeChange(ticketType.id, "price")}
                      error={!!errors[`ticketPrice-${ticketType.id}`]}
                      helperText={errors[`ticketPrice-${ticketType.id}`] || ""}
                      required
                      tooltipText="The price of this ticket type in dollars"
                    />

                    <InputField
                      id={`ticket-quantity-${ticketType.id}`}
                      name="quantity"
                      label="Quantity"
                      value={ticketType.quantity}
                      onChange={handleTicketTypeChange(
                        ticketType.id,
                        "quantity"
                      )}
                      error={!!errors[`ticketQuantity-${ticketType.id}`]}
                      helperText={
                        errors[`ticketQuantity-${ticketType.id}`] || ""
                      }
                      required
                      tooltipText="How many tickets of this type are available"
                    />

                    <div className="md:col-span-2">
                      <InputField
                        id={`ticket-description-${ticketType.id}`}
                        name="description"
                        label="Description"
                        value={ticketType.description}
                        onChange={handleTicketTypeChange(
                          ticketType.id,
                          "description"
                        )}
                        error={!!errors[`ticketDescription-${ticketType.id}`]}
                        helperText={
                          errors[`ticketDescription-${ticketType.id}`] || ""
                        }
                        multiline
                        rows={2}
                        tooltipText="Optional description of what's included with this ticket type"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </FormSection>
  );
};

export default TicketsSection;
